import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { GameCommand, MemberView } from "../../domain/types";
import { ApiError, api, loadRoomAccess } from "../api";

export type ConnectionState = "connecting" | "connected" | "reconnecting" | "offline" | "polling";

export function useRoom(roomId: string) {
  const access = useMemo(() => loadRoomAccess(roomId), [roomId]);
  const [view, setView] = useState<MemberView>();
  const viewRef = useRef<MemberView | undefined>(undefined);
  const [error, setError] = useState("");
  const [connection, setConnection] = useState<ConnectionState>("connecting");
  const [pending, setPending] = useState(false);

  useEffect(() => {
    viewRef.current = view;
  }, [view]);

  const reload = useCallback(async () => {
    if (!access) return;
    try {
      const next = await api.snapshot(roomId, access.memberToken);
      setView(next);
      setError("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "No pudimos recuperar la sala.");
    }
  }, [roomId, access]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    if (!access) return;
    let socket: WebSocket | undefined;
    let pingTimer = 0;
    let pollTimer = 0;
    let cancelled = false;
    let wsActive = false;

    const schedulePoll = (delay = document.visibilityState === "visible" ? 1_200 : 4_000) => {
      window.clearTimeout(pollTimer);
      if (cancelled || wsActive) return;
      pollTimer = window.setTimeout(poll, delay);
    };

    const poll = async () => {
      if (cancelled || wsActive) return;
      try {
        const next = await api.snapshot(roomId, access.memberToken);
        if (cancelled || wsActive) return;
        const current = viewRef.current;
        if (
          !current ||
          next.room.revision !== current.room.revision ||
          next.room.status !== current.room.status ||
          next.room.deviceMode !== current.room.deviceMode ||
          next.node.id !== current.node.id ||
          next.members.length !== current.members.length ||
          JSON.stringify(next.members) !== JSON.stringify(current.members) ||
          JSON.stringify(next.node) !== JSON.stringify(current.node)
        ) {
          setView(next);
        }
        setConnection("polling");
      } catch {
        if (!cancelled && !wsActive) setConnection(navigator.onLine ? "polling" : "offline");
      } finally {
        schedulePoll();
      }
    };

    const onVisibilityChange = () => {
      if (!wsActive && !cancelled && document.visibilityState === "visible") {
        poll();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    // Start polling immediately
    schedulePoll(100);

    // Also attempt WebSocket if available
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${protocol}//${window.location.host}/ws?roomId=${encodeURIComponent(roomId)}&memberToken=${encodeURIComponent(access.memberToken)}`;
    try {
      socket = new WebSocket(url);
      socket.onopen = () => {
        wsActive = true;
        window.clearTimeout(pollTimer);
        setConnection("connected");
        pingTimer = window.setInterval(() => {
          if (socket?.readyState === WebSocket.OPEN) socket.send("ping");
        }, 20_000);
      };
      socket.onmessage = (event) => {
        if (event.data === "pong") return;
        try {
          const payload = JSON.parse(event.data);
          if (payload.type === "snapshot" && payload.view) setView(payload.view);
        } catch {
          // Ignore invalid messages
        }
      };
      socket.onclose = () => {
        wsActive = false;
        window.clearInterval(pingTimer);
        if (!cancelled) {
          setConnection(navigator.onLine ? "polling" : "offline");
          schedulePoll(500);
        }
      };
      socket.onerror = () => {
        wsActive = false;
        socket?.close();
      };
    } catch {
      wsActive = false;
    }

    return () => {
      cancelled = true;
      wsActive = false;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.clearInterval(pingTimer);
      window.clearTimeout(pollTimer);
      socket?.close();
    };
  }, [roomId, access]);

  const send = useCallback(async (type: GameCommand["type"], payload: Record<string, unknown> = {}) => {
    if (!access || !viewRef.current || pending) return undefined;
    setPending(true);
    setError("");
    const command: GameCommand = {
      commandId: crypto.randomUUID(),
      expectedRevision: viewRef.current.room.revision,
      type,
      payload: type === "start_experience"
        ? { ...payload }
        : { ...payload, nodeId: viewRef.current.node.id }
    };
    try {
      const result = await api.command(roomId, access.memberToken, command);
      setView(result.view);
      return result;
    } catch (reason) {
      if (reason instanceof ApiError && (reason.code === "REVISION_CONFLICT" || reason.code === "NODE_CONFLICT")) {
        await reload();
        setError("La sala avanzó en otro teléfono. Ya mostramos la escena más reciente.");
      } else {
        setError(reason instanceof Error ? reason.message : "No pudimos guardar la acción.");
      }
      return undefined;
    } finally {
      setPending(false);
    }
  }, [access, pending, reload, roomId]);

  return { access, view, error, setError, connection, pending, reload, send };
}
